import React, { useEffect, useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { adminApi, AccountRecord } from '../service/adminApiService';
import { SearchInput } from '../components/SearchInput';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

export const AccountsCatalogPage: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await adminApi.listAccounts();
        if (cancelled) return;
        setAccounts(resp.accounts);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load accounts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    const filtered = search
      ? accounts.filter((a) => {
          const q = search.toLowerCase();
          return (
            a.account_id.includes(q) ||
            a.account_name.toLowerCase().includes(q) ||
            a.department.toLowerCase().includes(q)
          );
        })
      : accounts;
    const byDept = new Map<string, AccountRecord[]>();
    for (const a of filtered) {
      const dept = a.department || '(no department)';
      const arr = byDept.get(dept) || [];
      arr.push(a);
      byDept.set(dept, arr);
    }
    return Array.from(byDept.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [accounts, search]);

  return (
    <Column spacing="large">
      <PageHeader
        title="Account catalog"
        subtitle="Read-only reference view sourced from the Instance Registry."
        actions={
          <div style={{ minWidth: 260 }}>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by ID, name, or department…"
              ariaLabel="Search accounts"
            />
          </div>
        }
      />

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <Row alignmentHorizontal="center" spacingInset="500">
          <Loader type="circular" size="large" />
        </Row>
      ) : grouped.length === 0 ? (
        <Alert type="neutral">No accounts match the current search.</Alert>
      ) : (
        grouped.map(([dept, rows]) => (
          <Column key={dept} spacing="small">
            <Heading level={4}>{dept}</Heading>
            <Table headerRows={1} showDividers={true} aria-label={`Accounts in ${dept}`}>
              <TableRow>
                <TableCell>Account ID</TableCell>
                <TableCell>Account name</TableCell>
                <TableCell>Environment</TableCell>
              </TableRow>
              {rows.map((a) => (
                <TableRow key={a.account_id}>
                  <TableCell>
                    <Text type="b200">{a.account_id}</Text>
                  </TableCell>
                  <TableCell>{a.account_name || '-'}</TableCell>
                  <TableCell>{a.environment || '-'}</TableCell>
                </TableRow>
              ))}
            </Table>
          </Column>
        ))
      )}
    </Column>
  );
};
